import React from 'react';
import { useWindowTitle } from '../../hooks';
import { tabsPath } from '../components';
/**
 * Manages the state and behavior of tabs in the global layout.
 *
 * @param {NavigationInterface} navigationContext - The navigation context used for transitions.
 * @param {NavigateFunction} navigate - React Router navigate function.
 * @param {Location} location - Current location from React Router.
 * @param {Tab[]=} initialTabs - Optional initial list of open tabs.
 * @param {(closedTab: Tab, nextSelectedTab?: Tab) => void=} onCloseTab - Callback triggered when a tab is closed.
 * @param {(closedTabs: Tab[]) => void=} onCloseAllTabs - Callback triggered when all tabs are closed.
 * @param {(changedTabs: Tab[]) => void=} onChangeTabs - Callback triggered when the tab list changes.
 * @param {GlobalTabsBarActionItem[]=} additionalTabBarActions - Optional additional tab bar action buttons.
 *
 * @returns {{
 *   selectedTabPath?: string;
 *   tabs?: Tab[];
 *   setTabs: Dispatch<React.SetStateAction<Tab[] | undefined>>;
 *   handleOpenTab: (tab: Tab) => void;
 *   handleCloseTab: (closedTab: Tab, nextSelectedTab?: Tab) => void;
 *   handleCloseAllTabs: () => void;
 *   handleChangeTabs: (tabs: Tab[]) => void;
 *   additionalTabBarActions?: GlobalTabsBarActionItem[];
 * }} Object containing tab state and handlers for managing tab operations.
 */
export function useTabsState(navigationContext, navigate, location, initialTabs, onCloseTab, onCloseAllTabs, onChangeTabs, additionalTabBarActions) {
    var _a;
    const [tabs, setTabs] = React.useState(initialTabs);
    React.useEffect(() => {
        setTabs(tabs);
    }, [tabs]);
    const selectedTab = getSelectedTab(location.pathname, tabs);
    useWindowTitle((_a = selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.label) !== null && _a !== void 0 ? _a : '', 5);
    return {
        selectedTabPath: selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.path,
        tabs: tabs,
        setTabs: setTabs,
        handleOpenTab: (tab) => {
            navigationContext.navigate(() => {
                navigate(`${tabsPath}/` + tab.path);
            });
        },
        handleCloseTab: (closedTab, nextSelectedTab) => {
            navigationContext.navigate(() => {
                var _a;
                if (nextSelectedTab) {
                    navigate(`${tabsPath}/` + nextSelectedTab.path);
                }
                else {
                    if (selectedTab) {
                        navigate('/');
                    }
                }
                onCloseTab === null || onCloseTab === void 0 ? void 0 : onCloseTab(closedTab, nextSelectedTab);
                (_a = closedTab.onClose) === null || _a === void 0 ? void 0 : _a.call(closedTab, nextSelectedTab);
                setTabs((prevState) => (prevState !== null && prevState !== void 0 ? prevState : []).filter((tab) => tab.path !== closedTab.path));
            });
        },
        handleCloseAllTabs: () => {
            navigationContext.navigate(() => {
                if (selectedTab) {
                    navigate('/');
                }
                setTabs((prevState) => {
                    if (prevState) {
                        onCloseAllTabs === null || onCloseAllTabs === void 0 ? void 0 : onCloseAllTabs(prevState);
                        return [];
                    }
                    else {
                        return prevState;
                    }
                });
            });
        },
        handleChangeTabs: (tabs) => {
            onChangeTabs === null || onChangeTabs === void 0 ? void 0 : onChangeTabs(tabs);
            setTabs(tabs);
        },
        additionalTabBarActions: additionalTabBarActions,
    };
}
function getSelectedTab(currentPath, tabs) {
    let selectedTab = undefined;
    if (currentPath.startsWith(`/${tabsPath}/`)) {
        const currentTabPath = currentPath.substring(tabsPath.length + 2);
        for (const tab of tabs !== null && tabs !== void 0 ? tabs : []) {
            if (currentTabPath === tab.path || currentTabPath.startsWith(tab.path + '/')) {
                selectedTab = tab;
                break;
            }
        }
    }
    return selectedTab;
}
